/**
 * Gate for the aggressive in-app review auto-prompt: when an order's status
 * transitions to Delivered/Completed, we auto-navigate the customer to the
 * review screen — but only ONCE per order. Prompted order ids are persisted
 * to localStorage (scoped by tenant slug, mirroring other storage keys in
 * src/lib/participant-token.ts / hooks/usePushToken.ts) so the gate survives
 * reloads and doesn't nag the customer on every visit to the order.
 */

import { scopedStorageKey } from "./storageScope";
import { OrderStatusEnum } from "@/api/online-ordering-service/model";

const PROMPTED_KEY = "tappya.reviewPrompted";

/** Statuses that should trigger the auto-prompt. Explicitly NOT Cancelled/Rejected. */
export function isReviewPromptEligible(status: OrderStatusEnum | undefined | null): boolean {
  return status === OrderStatusEnum.Delivered || status === OrderStatusEnum.Completed;
}

function readPromptedSet(): Set<string> {
  try {
    const raw = localStorage.getItem(scopedStorageKey(PROMPTED_KEY));
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? new Set(parsed.filter((x) => typeof x === "string")) : new Set();
  } catch {
    return new Set();
  }
}

function writePromptedSet(ids: Set<string>): void {
  try {
    localStorage.setItem(scopedStorageKey(PROMPTED_KEY), JSON.stringify([...ids]));
  } catch {
    /* storage unavailable — degrade to "always eligible", never crash */
  }
}

/** Whether this order has already been auto-prompted for a review. */
export function hasPromptedReview(orderId: string | null | undefined): boolean {
  if (!orderId) return false;
  return readPromptedSet().has(orderId);
}

/** Marks the order as prompted so it is never auto-prompted again. */
export function markReviewPrompted(orderId: string | null | undefined): void {
  if (!orderId) return;
  const set = readPromptedSet();
  set.add(orderId);
  writePromptedSet(set);
}

/**
 * Pure decision function for the auto-prompt gate — kept side-effect-free so
 * it's trivial to unit test. Returns true iff this exact call should fire the
 * auto-prompt (and the caller should immediately call markReviewPrompted).
 */
export function shouldAutoPromptReview(params: {
  orderId: string | null | undefined;
  status: OrderStatusEnum | undefined | null;
}): boolean {
  if (!params.orderId) return false;
  if (!isReviewPromptEligible(params.status)) return false;
  return !hasPromptedReview(params.orderId);
}

/**
 * Minimal shape the app-level "pick an order to prompt for" selector needs —
 * kept narrow (not the full OrderOutput) so it's trivial to construct in tests.
 */
export type ReviewPromptCandidate = {
  id: string | null | undefined;
  status: OrderStatusEnum | undefined | null;
  /** Best-available "when did this order finish" timestamp, ISO string. */
  completedAt?: string | null;
};

/**
 * Picks the single most recent eligible-and-unprompted order out of a list,
 * for the app-level open/resume prompt (as opposed to the single-order gate
 * above used by OrderDetailScreen). Pure + side-effect-free: does not itself
 * call markReviewPrompted — the caller does that once the modal is actually
 * shown/dismissed.
 *
 * Returns null when nothing qualifies (not logged in / no orders / all
 * already prompted / none terminal-and-unreviewed).
 */
export function selectOrderToPromptForReview(
  orders: ReviewPromptCandidate[] | null | undefined,
): ReviewPromptCandidate | null {
  if (!orders || orders.length === 0) return null;

  const eligible = orders.filter(
    (o) => !!o.id && isReviewPromptEligible(o.status) && !hasPromptedReview(o.id),
  );
  if (eligible.length === 0) return null;

  const timeOf = (o: ReviewPromptCandidate): number => {
    const t = o.completedAt ? Date.parse(o.completedAt) : NaN;
    return Number.isNaN(t) ? 0 : t;
  };

  return eligible.reduce((latest, candidate) => (timeOf(candidate) > timeOf(latest) ? candidate : latest));
}
